import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../stores/language.store';
import {
  X, ChevronLeft, Shield, ShieldCheck, CheckCircle2,
  Loader2, Home as HomeIcon, PlusCircle,
  AlertCircle, Check, Calendar,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Sheet, SheetContent } from '../ui/Sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Asset, saveManualWarranty,
} from '../../services/asset.api';
import { useProperty } from '../../contexts/PropertyContext';
import {
  ExtProviderType, ExtThirdPartySubType, ExtServiceType, SERVICE_TYPE_LABELS,
  RetailerProvider, ManufacturerProvider, ManufacturerPlanTier, ThirdPartyProvider,
  kRetailerProviders, kManufacturerProviders, kPerItemProviders, kSubscriptionProviders, kHomeWarrantyProviders,
} from './warrantyProviders';

// ── Constants ─────────────────────────────────────────────────────────────────

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DEDUCTIBLE_CHIPS = [0, 25, 50, 75, 100];

const GENERIC_MFG_PLANS: ManufacturerPlanTier[] = [
  { id: 'generic_parts_labor', name: 'Parts & Labor Warranty', durationOptions: [1, 2, 3, 4, 5], deductibleAmount: 0, defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false },
  { id: 'generic_parts_only', name: 'Parts Only Warranty', durationOptions: [1, 2, 3, 4, 5], deductibleAmount: 0, defaultServiceType: 'depot', startsOnPurchaseDate: false, supportsMonthly: false },
  { id: 'generic_limited', name: 'Limited Extended Warranty', durationOptions: [1, 2, 3], deductibleAmount: 0, defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false },
];

// ── Types ─────────────────────────────────────────────────────────────────────

type StepId = 'purchase-date' | 'coverage' | 'property' | 'review';

interface AddWarrantyDrawerProps {
  open: boolean;
  onClose: () => void;
  asset: Asset;
  onSaved: (asset: Asset) => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function addYearsToDate(base: Date, years: number): Date {
  const d = new Date(base);
  d.setFullYear(d.getFullYear() + years);
  return d;
}

function fmtDate(d: Date | null | undefined, lang: string): string {
  if (!d) return '—';
  return new Intl.DateTimeFormat(lang, { month: 'long', day: 'numeric', year: 'numeric' }).format(d);
}

function getPurchaseDate(asset: Asset): Date | null {
  if (asset.purchasedAt) return new Date(asset.purchasedAt);
  if (asset.purchaseYear) return new Date(asset.purchaseYear, (asset.purchaseMonth ?? 1) - 1, 1);
  return null;
}

function findAssetManufacturer(asset: Asset): ManufacturerProvider | null {
  const raw = (asset.brand ?? asset.manufacturer ?? '').toLowerCase().trim();
  if (!raw) return null;
  return kManufacturerProviders.find(m =>
    m.brandAliases.some(a => {
      const alias = a.toLowerCase();
      return raw === alias || raw.startsWith(alias + ' ') || alias.startsWith(raw);
    }),
  ) ?? null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AddWarrantyDrawer({ open, onClose, asset, onSaved }: Readonly<AddWarrantyDrawerProps>) {
  const { t } = useTranslation('warranty');
  const { language } = useLanguageStore();
  const fmt = (d: Date | null | undefined) => fmtDate(d, language);
  const { properties } = useProperty();
  const currentYear = new Date().getFullYear();

  const hasPurchaseDate = !!(asset.purchasedAt || asset.purchaseYear);
  const hasDeliveryAddress = !!asset.deliveryAddress;
  const assetManufacturer = findAssetManufacturer(asset);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const [currentStepId, setCurrentStepId] = useState<StepId>(
    hasPurchaseDate ? 'coverage' : 'purchase-date',
  );

  // ── Purchase date ───────────────────────────────────────────────────────────
  const [purchaseYear, setPurchaseYear] = useState<number>(currentYear);
  const [purchaseMonth, setPurchaseMonth] = useState<number | null>(null);

  // ── Coverage ────────────────────────────────────────────────────────────────
  const mfgEnabled = true;
  const [extEnabled, setExtEnabled] = useState(false);

  // Extended plan — provider type
  const [extProviderType, setExtProviderType] = useState<ExtProviderType | null>(null);

  // Extended plan — retailer
  const [extRetailerId, setExtRetailerId] = useState<string | null>(null);
  const [extRetailerName, setExtRetailerName] = useState<string | null>(null);
  const [extRetailerCustomName, setExtRetailerCustomName] = useState('');
  const [extRetailerPlanBrandName, setExtRetailerPlanBrandName] = useState<string | null>(null);

  // Extended plan — manufacturer
  const [extManufacturerId, setExtManufacturerId] = useState<string | null>(null);
  const [extManufacturerName, setExtManufacturerName] = useState<string | null>(null);
  const [extManufacturerCustomName, setExtManufacturerCustomName] = useState('');
  const [extPlanTierId, setExtPlanTierId] = useState<string | null>(null);
  const [extPlanTierName, setExtPlanTierName] = useState<string | null>(null);

  // Extended plan — third party
  const [extThirdPartyId, setExtThirdPartyId] = useState<string | null>(null);
  const [extThirdPartyName, setExtThirdPartyName] = useState<string | null>(null);
  const [extThirdPartySubType, setExtThirdPartySubType] = useState<ExtThirdPartySubType | null>(null);

  // Extended plan — coverage details
  const [extDurationYears, setExtDurationYears] = useState<number | null>(null);
  const [extCustomDuration, setExtCustomDuration] = useState(false);
  const [extIsSubscription, setExtIsSubscription] = useState(false);
  const [extMonthlyFee, setExtMonthlyFee] = useState<number | null>(null);
  const [extStartsOnPurchaseDate, setExtStartsOnPurchaseDate] = useState(true);
  const [extServiceType, setExtServiceType] = useState<ExtServiceType>('inHome');
  const [extDeductibleAmount, setExtDeductibleAmount] = useState<number | null>(null);
  const [extContractNumber, setExtContractNumber] = useState('');
  const [extUserStartDate, setExtUserStartDate] = useState('');
  const [extClaimsPhone, setExtClaimsPhone] = useState('');
  const [extClaimsWebsite, setExtClaimsWebsite] = useState('');

  // ── Property ────────────────────────────────────────────────────────────────
  const [propertyAction, setPropertyAction] = useState<'keep' | 'move' | 'create'>('keep');
  const [targetHomeId, setTargetHomeId] = useState('');

  // ── Save ────────────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedSuccess, setSavedSuccess] = useState(false);

  // ── Computed: steps ─────────────────────────────────────────────────────────
  const needsTierStep = (
    extProviderType === 'manufacturer' &&
    extManufacturerId !== null &&
    extManufacturerId !== 'other' &&
    (kManufacturerProviders.find(m => m.id === extManufacturerId)?.isMultiTier ?? false)
  );

  const steps: StepId[] = [
    ...(hasPurchaseDate ? [] : ['purchase-date' as StepId]),
    'coverage',
    ...(hasDeliveryAddress ? ['property' as StepId] : []),
    'review',
  ];

  const currentStep = steps.includes(currentStepId) ? currentStepId : (steps.at(-1) ?? 'review');
  const currentStepIdx = steps.indexOf(currentStep);

  // ── Computed: dates ─────────────────────────────────────────────────────────
  const resolvedPurchaseDate: Date = (() => {
    const existing = getPurchaseDate(asset);
    if (existing) return existing;
    return new Date(purchaseYear, purchaseMonth ?? 0, 1);
  })();

  const extManufacturerLookup: ManufacturerProvider | null =
    extProviderType === 'manufacturer' && extManufacturerId === null && extManufacturerCustomName.trim()
      ? (kManufacturerProviders.find(m =>
          m.brandAliases.some(a =>
            extManufacturerCustomName.toLowerCase().includes(a.toLowerCase()) ||
            a.toLowerCase().includes(extManufacturerCustomName.toLowerCase()),
          ),
        ) ?? null)
      : null;

  const mfgExpiry = addYearsToDate(resolvedPurchaseDate, 1);
  const extStartDate = extStartsOnPurchaseDate ? resolvedPurchaseDate : mfgExpiry;
  const extExpiry = (extEnabled && extDurationYears && extDurationYears > 0)
    ? addYearsToDate(extStartDate, extDurationYears)
    : null;

  // ── Reset on open ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setCurrentStepId(hasPurchaseDate ? 'coverage' : 'purchase-date');
    setPurchaseYear(currentYear);
    setPurchaseMonth(null);
    setExtEnabled(false);
    setExtProviderType(null);
    setExtRetailerId(null); setExtRetailerName(null); setExtRetailerCustomName(''); setExtRetailerPlanBrandName(null);
    setExtManufacturerId(null); setExtManufacturerName(null); setExtManufacturerCustomName(''); setExtPlanTierId(null); setExtPlanTierName(null);
    setExtThirdPartyId(null); setExtThirdPartyName(null); setExtThirdPartySubType(null);
    setExtDurationYears(null); setExtCustomDuration(false); setExtIsSubscription(false); setExtMonthlyFee(null);
    setExtStartsOnPurchaseDate(true); setExtServiceType('inHome'); setExtDeductibleAmount(null);
    setExtContractNumber(''); setExtUserStartDate(''); setExtClaimsPhone(''); setExtClaimsWebsite('');
    setPropertyAction('keep'); setTargetHomeId('');
    setSaving(false); setSaveError(null); setSavedSuccess(false);
  }, [open, currentYear, hasPurchaseDate]);

  // ── Default-applicators ─────────────────────────────────────────────────────
  const applyRetailerDefaults = (retailer: RetailerProvider) => {
    setExtDurationYears(retailer.durationOptions[0]);
    setExtCustomDuration(false);
    setExtDeductibleAmount(retailer.deductibleAmount);
    setExtServiceType(retailer.defaultServiceType);
    setExtIsSubscription(false);
    setExtMonthlyFee(null);
    setExtStartsOnPurchaseDate(true);
    setExtClaimsPhone(retailer.claimsPhone);
    setExtClaimsWebsite(retailer.claimsWebsite);
  };

  const applyManufacturerTierDefaults = (tier: ManufacturerPlanTier) => {
    setExtPlanTierId(tier.id);
    setExtPlanTierName(tier.name);
    setExtDurationYears(tier.durationOptions[0]);
    setExtCustomDuration(false);
    setExtDeductibleAmount(tier.deductibleAmount);
    setExtServiceType(tier.defaultServiceType);
    setExtIsSubscription(false);
    setExtMonthlyFee(null);
    setExtStartsOnPurchaseDate(tier.startsOnPurchaseDate);
  };

  const applyThirdPartyDefaults = (provider: ThirdPartyProvider) => {
    if (provider.isSubscription) {
      setExtIsSubscription(true);
      setExtMonthlyFee(provider.monthlyFee ?? null);
      setExtDurationYears(null);
      setExtCustomDuration(false);
    } else {
      setExtIsSubscription(false);
      setExtDurationYears(provider.durationOptions?.[0] ?? null);
      setExtCustomDuration(false);
      setExtMonthlyFee(null);
    }
    setExtDeductibleAmount(provider.deductibleAmount);
    setExtServiceType(provider.defaultServiceType);
    setExtStartsOnPurchaseDate(true);
    setExtClaimsPhone(provider.claimsPhone);
    setExtClaimsWebsite(provider.claimsWebsite);
  };

  // ── Navigation ──────────────────────────────────────────────────────────────
  const goNext = () => {
    const idx = steps.indexOf(currentStep);
    if (idx < steps.length - 1) setCurrentStepId(steps[idx + 1]);
  };

  const goBack = () => {
    const idx = steps.indexOf(currentStep);
    if (idx > 0) setCurrentStepId(steps[idx - 1]);
  };

  const canGoNext = (): boolean => {
    switch (currentStep) {
      case 'purchase-date':
        return purchaseMonth !== null && purchaseYear <= currentYear;
      case 'coverage': {
        if (!extEnabled) return true;
        if (!extProviderType) return false;
        if (extProviderType === 'retailer') {
          if (!extRetailerId) return false;
          if (extRetailerId === 'other' && !extRetailerCustomName.trim()) return false;
          return extDurationYears !== null;
        }
        if (extProviderType === 'manufacturer') {
          if (!extManufacturerId) return false;
          if (extManufacturerId === 'other' && !extManufacturerCustomName.trim()) return false;
          if (needsTierStep && !extPlanTierId) return false;
          return extIsSubscription || extDurationYears !== null;
        }
        if (extProviderType === 'thirdParty') {
          if (!extThirdPartyId) return false;
          return extIsSubscription || extDurationYears !== null;
        }
        return false;
      }
      case 'property':
        return propertyAction === 'move' ? !!targetHomeId : true;
      default:
        return true;
    }
  };

  // ── Build extendedPlanData ──────────────────────────────────────────────────
  const buildExtendedPlanData = (): Record<string, unknown> | undefined => {
    if (!extEnabled || extProviderType === null) return undefined;

    const providerDisplayName: string | undefined = (() => {
      if (extProviderType === 'retailer') {
        if (extRetailerId === 'other') return extRetailerCustomName || undefined;
        return extRetailerPlanBrandName ?? extRetailerName ?? undefined;
      }
      if (extProviderType === 'manufacturer') {
        if (extManufacturerId === 'other') return extManufacturerCustomName || undefined;
        return extPlanTierName ?? extManufacturerName ?? undefined;
      }
      return extThirdPartyName ?? undefined;
    })();

    return {
      providerType: extProviderType,
      providerDisplayName,
      retailerId: extRetailerId,
      retailerName: extRetailerName,
      retailerCustomName: extRetailerCustomName || undefined,
      retailerPlanBrandName: extRetailerPlanBrandName,
      manufacturerId: extManufacturerId,
      manufacturerName: extManufacturerId === 'other' ? extManufacturerCustomName || null : extManufacturerName,
      planTierId: extPlanTierId,
      planTierName: extPlanTierName,
      thirdPartyProviderId: extThirdPartyId,
      thirdPartyProviderName: extThirdPartyName,
      thirdPartySubType: extThirdPartySubType,
      durationYears: extDurationYears,
      isSubscription: extIsSubscription,
      monthlyFee: extMonthlyFee,
      startsOnPurchaseDate: extStartsOnPurchaseDate,
      serviceType: extServiceType,
      deductibleAmount: extDeductibleAmount,
      contractNumber: extContractNumber || undefined,
      claimsPhone: extClaimsPhone || undefined,
      claimsWebsite: extClaimsWebsite || undefined,
      startDate: extUserStartDate || extStartDate.toISOString(),
      extendedWarrantyStartDate: extUserStartDate || undefined,
      expiryDate: extExpiry?.toISOString(),
    };
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const extProviderLegacy: string | undefined = (() => {
        if (!extEnabled) return undefined;
        if (extProviderType === 'retailer') return (extRetailerPlanBrandName ?? extRetailerName ?? extRetailerCustomName) || undefined;
        if (extProviderType === 'manufacturer') return extPlanTierName ?? (extManufacturerId === 'other' ? extManufacturerCustomName : extManufacturerName) ?? undefined;
        return extThirdPartyName ?? undefined;
      })();

      const updated = await saveManualWarranty(asset.id, {
        purchasedAt: hasPurchaseDate
          ? undefined
          : new Date(purchaseYear, purchaseMonth ?? 0, 1).toISOString(),
        mfgEnabled,
        extEnabled,
        extProvider: extProviderLegacy,
        extYears: extEnabled && extDurationYears ? extDurationYears : undefined,
        extendedPlanData: buildExtendedPlanData(),
        extendedWarrantyStartDate: extUserStartDate || undefined,
        targetHomeId: propertyAction === 'move' && targetHomeId ? targetHomeId : undefined,
        createNewHome: propertyAction === 'create',
        deliveryAddress: propertyAction === 'create' ? (asset.deliveryAddress || undefined) : undefined,
        warrantySource: 'manual',
      });

      setSavedSuccess(true);
      setTimeout(() => { onSaved(updated); onClose(); }, 1400);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('addDrawer.errSave');
      setSaveError(msg);
      setSaving(false);
    }
  };

  if (!open) return null;

  // ── Step metadata ───────────────────────────────────────────────────────────
  const stepLabels: Record<StepId, string> = {
    'purchase-date': t('addDrawer.labelPurchaseDate'),
    'coverage':      t('addDrawer.labelCoverage'),
    'property':      t('addDrawer.labelProperty'),
    'review':        t('addDrawer.labelReview'),
  };

  const extDisplayName: string = (() => {
    if (!extEnabled || extProviderType === null) return '';
    if (extProviderType === 'retailer') {
      if (extRetailerId === 'other') return extRetailerCustomName || 'Custom Plan';
      return extRetailerPlanBrandName ?? extRetailerName ?? 'Retailer Plan';
    }
    if (extProviderType === 'manufacturer') {
      if (extManufacturerId === 'other') return extManufacturerCustomName || 'Custom Plan';
      return extPlanTierName ?? extManufacturerName ?? 'Manufacturer Plan';
    }
    return extThirdPartyName ?? 'Third Party Plan';
  })();

  // ── Success state ───────────────────────────────────────────────────────────
  if (savedSuccess) {
    return (
      <Sheet open={open} onOpenChange={() => onClose()}>
        <SheetContent side="right" className="w-full sm:max-w-lg [&>button]:hidden p-0">
          <div className="flex flex-col items-center justify-center h-full gap-5 py-16 px-6">
            <div className="h-20 w-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Check className="h-10 w-10 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-center">
              <p className="text-xl font-semibold text-foreground mb-1">{t('addDrawer.savedTitle')}</p>
              <p className="text-sm text-muted-foreground">{t('addDrawer.savedDesc')}</p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // ── Helper components ──────────────────────────────────────────────────────
  const SectionIntro = ({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) => (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-foreground/5 flex items-center justify-center shrink-0">{icon}</div>
      <div>
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>
      </div>
    </div>
  );

  const ReviewRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm font-semibold text-foreground text-right truncate">{value}</span>
    </div>
  );

  // ── JSX ────────────────────────────────────────────────────────────────────
  return (
    <Sheet open={open} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg [&>button]:hidden p-0 flex flex-col h-full">
        {/* ── Header ── */}
        <div className="sticky top-0 bg-background z-10 border-b px-6 py-4 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-foreground/5 rounded-lg">
                <ShieldCheck className="h-5 w-5 text-foreground" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground leading-none">Add Warranty</h2>
                <p className="text-xs text-muted-foreground mt-1">Step {currentStepIdx + 1} of {steps.length} — {stepLabels[currentStep]}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} disabled={saving} className="h-8 w-8">
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex gap-1.5 mt-4">
            {steps.map((s, i) => (
              <div
                key={s}
                className={cn(
                  'h-1 flex-1 rounded-full transition-colors',
                  i < currentStepIdx ? 'bg-green-500' : i === currentStepIdx ? 'bg-foreground' : 'bg-muted-foreground/20',
                )}
              />
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* ── Purchase Date ── */}
          {currentStep === 'purchase-date' && (
            <div className="space-y-5">
              <SectionIntro icon={<Calendar className="h-5 w-5 text-foreground" />} title={stepLabels['purchase-date']} subtitle={t('addDrawer.descPurchaseDate')} />

              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('addDrawer.year')}</label>
                <div className="flex items-center gap-4 bg-secondary/20 rounded-xl px-5 py-4 border border-border">
                  <button type="button" onClick={() => setPurchaseYear(y => Math.max(1990, y - 1))} className="h-9 w-9 rounded-xl bg-card border border-border flex items-center justify-center hover:bg-secondary transition-colors font-semibold text-foreground text-base">-</button>
                  <span className="flex-1 text-center text-3xl font-semibold text-foreground tabular-nums">{purchaseYear}</span>
                  <button type="button" onClick={() => setPurchaseYear(y => Math.min(currentYear, y + 1))} disabled={purchaseYear >= currentYear} className="h-9 w-9 rounded-xl bg-card border border-border flex items-center justify-center hover:bg-secondary transition-colors font-semibold text-foreground text-base disabled:opacity-30">+</button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('addDrawer.month')}</label>
                <div className="grid grid-cols-4 gap-2">
                  {MONTHS.map((m, i) => (
                    <button key={m} type="button" onClick={() => setPurchaseMonth(i)} className={cn('py-2.5 rounded-xl text-sm font-medium border transition-all', purchaseMonth === i ? 'bg-foreground text-background border-foreground' : 'text-foreground border-border hover:border-foreground/40 hover:bg-secondary/50 bg-card')}>{m.slice(0, 3)}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Coverage ── */}
          {currentStep === 'coverage' && (
            <div className="space-y-5">
              <SectionIntro icon={<Shield className="h-5 w-5 text-foreground" />} title={stepLabels['coverage']} subtitle={t('addDrawer.descCoverage')} />

              {/* Manufacturer warranty banner */}
              <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
                <p className="text-sm text-blue-700 dark:text-blue-300 font-medium flex-1">{t('addDrawer.mfgIncluded', { date: fmt(mfgExpiry) })}</p>
                <CheckCircle2 className="h-4 w-4 text-blue-500/60 shrink-0" />
              </div>

              {/* Extended protection toggle */}
              <button
                type="button"
                onClick={() => {
                  setExtEnabled(v => !v);
                  setExtProviderType(null);
                  setExtRetailerId(null); setExtRetailerName(null); setExtRetailerCustomName(''); setExtRetailerPlanBrandName(null);
                  setExtManufacturerId(null); setExtManufacturerName(null); setExtManufacturerCustomName(''); setExtPlanTierId(null); setExtPlanTierName(null);
                  setExtThirdPartyId(null); setExtThirdPartyName(null); setExtThirdPartySubType(null);
                  setExtDurationYears(null); setExtCustomDuration(false); setExtIsSubscription(false); setExtMonthlyFee(null);
                  setExtDeductibleAmount(null);
                }}
                className={cn(
                  'w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all duration-200',
                  extEnabled ? 'border-foreground bg-secondary' : 'border-border bg-card hover:border-muted-foreground/30',
                )}
              >
                <div className={cn('h-10 w-10 rounded-xl flex items-center justify-center shrink-0', extEnabled ? 'bg-foreground/10' : 'bg-muted')}>
                  <ShieldCheck className={cn('h-5 w-5', extEnabled ? 'text-foreground' : 'text-muted-foreground')} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">{t('addDrawer.extendedTitle')}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{extEnabled ? 'Tap to remove extended coverage' : t('addDrawer.extendedSub')}</p>
                </div>
                <div className={cn('relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors', extEnabled ? 'bg-foreground' : 'bg-muted-foreground/30')}>
                  <span className={cn('inline-block h-5 w-5 rounded-full bg-background shadow-sm transition-transform', extEnabled ? 'translate-x-5' : 'translate-x-0.5')} />
                </div>
              </button>

              {/* ── Extended form ── */}
              {extEnabled && (
                <div className="space-y-4 pt-1">

                  {/* Provider type */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t('addDrawer.extProviderPrompt')}</label>
                    <Select
                      value={extProviderType ?? ''}
                      onValueChange={v => {
                        const val = v as ExtProviderType | '';
                        setExtProviderType(val || null);
                        setExtRetailerId(null); setExtRetailerName(null); setExtRetailerCustomName(''); setExtRetailerPlanBrandName(null);
                        setExtPlanTierId(null); setExtPlanTierName(null);
                        setExtThirdPartyId(null); setExtThirdPartyName(null); setExtThirdPartySubType(null);
                        setExtDurationYears(null); setExtCustomDuration(false); setExtIsSubscription(false);
                        if (val === 'manufacturer') {
                          if (assetManufacturer) {
                            setExtManufacturerId(assetManufacturer.id);
                            setExtManufacturerName(assetManufacturer.name);
                            setExtManufacturerCustomName('');
                            setExtClaimsPhone(assetManufacturer.claimsPhone);
                            setExtClaimsWebsite(assetManufacturer.claimsWebsite);
                            if (!assetManufacturer.isMultiTier && assetManufacturer.defaultTier) {
                              applyManufacturerTierDefaults(assetManufacturer.defaultTier);
                            }
                          } else {
                            setExtManufacturerId('other');
                            setExtManufacturerName(null);
                            setExtManufacturerCustomName(asset.brand ?? asset.manufacturer ?? '');
                          }
                        } else {
                          setExtManufacturerId(null); setExtManufacturerName(null); setExtManufacturerCustomName('');
                        }
                      }}
                    >
                      <SelectTrigger className="w-full h-11 rounded-xl text-sm"><SelectValue placeholder={t('addDrawer.selectProviderPh')} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="retailer">{t('addDrawer.optRetailer')}</SelectItem>
                        <SelectItem value="manufacturer">{t('addDrawer.optManufacturer')}</SelectItem>
                        <SelectItem value="thirdParty">{t('addDrawer.optThirdParty')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Retailer */}
                  {extProviderType === 'retailer' && (
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">{t('addDrawer.selectRetailer')}</label>
                      <Select
                        value={extRetailerId ?? ''}
                        onValueChange={id => {
                          if (!id) { setExtRetailerId(null); setExtRetailerName(null); setExtRetailerPlanBrandName(null); return; }
                          if (id === 'other') { setExtRetailerId('other'); setExtRetailerName(null); setExtRetailerPlanBrandName(null); setExtDurationYears(2); setExtDeductibleAmount(0); setExtStartsOnPurchaseDate(true); return; }
                          const r = kRetailerProviders.find(rr => rr.id === id);
                          if (r) { setExtRetailerId(r.id); setExtRetailerName(r.name); setExtRetailerPlanBrandName(r.planBrandName); setExtRetailerCustomName(''); applyRetailerDefaults(r); }
                        }}
                      >
                        <SelectTrigger className="w-full h-11 rounded-xl text-sm"><SelectValue placeholder={t('addDrawer.selectRetailerPh')} /></SelectTrigger>
                        <SelectContent>
                          {kRetailerProviders.slice(0, 5).map(r => (<SelectItem key={r.id} value={r.id}>{r.name} — {r.planBrandName}</SelectItem>))}
                          <SelectItem value="other">{t('addDrawer.otherNotListed')}</SelectItem>
                        </SelectContent>
                      </Select>
                      {extRetailerId === 'other' && <Input value={extRetailerCustomName} onChange={e => setExtRetailerCustomName(e.target.value)} placeholder={t('addDrawer.retailerNamePh')} className="mt-2" />}
                    </div>
                  )}

                  {/* Manufacturer */}
                  {extProviderType === 'manufacturer' && (
                    <div className="space-y-3">
                      {assetManufacturer ? (
                        <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-secondary/20 border-2 border-foreground/20">
                          <div className="h-9 w-9 rounded-xl bg-foreground/10 flex items-center justify-center shrink-0"><ShieldCheck className="h-5 w-5 text-foreground" /></div>
                          <div className="flex-1"><p className="text-sm font-semibold text-foreground">{assetManufacturer.name}</p><p className="text-xs text-muted-foreground mt-0.5">{t('addDrawer.mfgFromAsset')}</p></div>
                          <CheckCircle2 className="h-5 w-5 text-foreground/40 shrink-0" />
                        </div>
                      ) : (
                        <div className="space-y-1.5"><label className="text-sm font-medium">{t('addDrawer.manufacturerName')}</label><Input value={extManufacturerCustomName} onChange={e => setExtManufacturerCustomName(e.target.value)} placeholder={asset.brand ?? asset.manufacturer ?? 'Enter manufacturer name'} /></div>
                      )}
                      {(() => {
                        const activeMfg = assetManufacturer ?? extManufacturerLookup;
                        let plans: ManufacturerPlanTier[];
                        if (!activeMfg) plans = GENERIC_MFG_PLANS;
                        else if (activeMfg.isMultiTier) plans = activeMfg.tiers;
                        else plans = activeMfg.defaultTier ? [activeMfg.defaultTier] : GENERIC_MFG_PLANS;
                        return (
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium">{t('addDrawer.selectWarrantyPlan')}</label>
                            <div className="flex flex-col gap-2">
                              {plans.map((plan, idx) => {
                                const sel = extPlanTierId === plan.id;
                                return (
                                  <button key={plan.id} type="button" onClick={() => applyManufacturerTierDefaults(plan)} className={cn('w-full flex items-start gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-all', sel ? 'border-foreground bg-secondary' : 'border-border bg-card hover:border-foreground/30 hover:bg-secondary/50')}>
                                    <ShieldCheck className={cn('h-4 w-4 mt-0.5 shrink-0', sel ? 'text-foreground' : 'text-muted-foreground')} />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <p className={cn('text-sm font-semibold', sel ? 'text-foreground' : 'text-foreground')}>{plan.name}</p>
                                        {idx === 0 && plans.length > 1 && <span className="px-1.5 py-0.5 rounded bg-foreground text-background text-[9px] font-bold uppercase">Popular</span>}
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-0.5">{SERVICE_TYPE_LABELS[plan.defaultServiceType]}{plan.deductibleAmount > 0 ? ` · $${plan.deductibleAmount} deductible` : ' · $0 deductible'}{plan.supportsMonthly ? ' · Monthly' : ''}</p>
                                    </div>
                                    {sel && <Check className="h-4 w-4 text-foreground shrink-0 mt-0.5" />}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Third party */}
                  {extProviderType === 'thirdParty' && (() => {
                    const allOpts: { id: string; label: string }[] = [
                      ...kPerItemProviders.slice(0, 3).map(p => ({ id: p.id, label: p.name })),
                      ...kSubscriptionProviders.slice(0, 1).map(p => ({ id: p.id, label: `${p.name} (Monthly)` })),
                      ...kHomeWarrantyProviders.slice(0, 1).map(p => ({ id: p.id, label: `${p.name} (Home Warranty)` })),
                    ];
                    return (
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium">{t('addDrawer.selectProvider')}</label>
                        <Select
                          value={extThirdPartyId ?? ''}
                          onValueChange={id => {
                            const opt = allOpts.find(o => o.id === id);
                            if (!opt) { setExtThirdPartyId(null); setExtThirdPartyName(null); setExtThirdPartySubType(null); return; }
                            const hw = kHomeWarrantyProviders.find(p => p.id === id);
                            const tp = [...kPerItemProviders, ...kSubscriptionProviders].find(p => p.id === id);
                            setExtThirdPartyId(opt.id); setExtThirdPartyName(tp?.name ?? hw?.name ?? '');
                            if (hw) { setExtThirdPartySubType('homeWarranty'); setExtIsSubscription(false); setExtDurationYears(1); setExtDeductibleAmount(hw.serviceFee); setExtServiceType('inHome'); setExtMonthlyFee(null); setExtStartsOnPurchaseDate(true); setExtClaimsPhone(hw.claimsPhone); setExtClaimsWebsite(hw.claimsWebsite); }
                            else if (tp) { setExtThirdPartySubType(tp.subType); applyThirdPartyDefaults(tp); }
                          }}
                        >
                          <SelectTrigger className="w-full h-11 rounded-xl text-sm"><SelectValue placeholder={t('addDrawer.selectProviderPh2')} /></SelectTrigger>
                          <SelectContent>{allOpts.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    );
                  })()}

                  {/* Duration chips */}
                  {extProviderType !== null && (extRetailerId !== null || extManufacturerId !== null || extThirdPartyId !== null) && !extIsSubscription && (() => {
                    const durationOpts: number[] = (() => {
                      if (extProviderType === 'retailer') return kRetailerProviders.find(r => r.id === extRetailerId)?.durationOptions ?? [1, 2, 3];
                      if (extProviderType === 'manufacturer') { if (extManufacturerId === 'other') return [1, 2, 3, 5]; const mfg = kManufacturerProviders.find(m => m.id === extManufacturerId); if (mfg?.isMultiTier) return mfg.tiers.find(t => t.id === extPlanTierId)?.durationOptions ?? []; return mfg?.defaultTier?.durationOptions ?? [1, 2, 3]; }
                      return kPerItemProviders.find(p => p.id === extThirdPartyId)?.durationOptions ?? [1, 2, 3];
                    })();
                    if (!durationOpts.length) return null;
                    const currentTier = extProviderType === 'manufacturer' && extManufacturerId !== 'other' ? (() => { const mfg2 = kManufacturerProviders.find(m => m.id === extManufacturerId); return mfg2?.isMultiTier ? mfg2.tiers.find(t => t.id === extPlanTierId) ?? null : mfg2?.defaultTier ?? null; })() : null;
                    return (
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium">{t('addDrawer.coverageDuration')}</label>
                        <div className="flex flex-wrap gap-2">
                          {durationOpts.map(yr => (<button key={yr} type="button" onClick={() => setExtDurationYears(yr)} className={cn('px-4 py-2 rounded-xl text-sm font-medium border transition-all', extDurationYears === yr && !extCustomDuration ? 'bg-foreground text-background border-foreground' : 'text-foreground border-border hover:border-foreground/40 hover:bg-secondary/50 bg-card')}>{yr === 1 ? '1 yr' : `${yr} yrs`}</button>))}
                          <button type="button" onClick={() => { setExtCustomDuration(true); setExtDurationYears(null); }} className={cn('px-4 py-2 rounded-xl text-sm font-medium border transition-all', extCustomDuration ? 'bg-foreground text-background border-foreground' : 'text-foreground border-border hover:border-foreground/40 hover:bg-secondary/50 bg-card')}>+</button>
                          {currentTier?.supportsMonthly && <button type="button" onClick={() => { setExtIsSubscription(true); setExtDurationYears(null); setExtCustomDuration(false); }} className="px-4 py-2 rounded-xl text-sm font-medium border border-border text-foreground hover:border-foreground/40 hover:bg-secondary/50 bg-card transition-all">{t('addDrawer.monthly')}</button>}
                        </div>
                        {extCustomDuration && <div className="flex items-center gap-2 mt-2"><Input value={extDurationYears ?? ''} onChange={e => { const raw = e.target.value.replace(/\D/g, ''); const val = raw ? Number(raw) : null; setExtDurationYears(val && val > 0 ? val : null); }} placeholder="0" className="w-24" /><span className="text-xs text-muted-foreground">years</span></div>}
                        {extDurationYears && extExpiry && <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1"><Calendar className="h-3 w-3" /> {t('addDrawer.expiresLabel', { date: fmt(extExpiry) })}</p>}
                      </div>
                    );
                  })()}

                  {extIsSubscription && <div className="flex items-center justify-between rounded-xl bg-secondary/20 border border-border px-4 py-3"><p className="text-xs font-medium">{t('addDrawer.monthlySubNote')}</p><button type="button" onClick={() => { setExtIsSubscription(false); setExtCustomDuration(false); }} className="text-xs text-blue-500 font-medium hover:underline">{t('addDrawer.change')}</button></div>}

                  {/* Coverage Start Date */}
                  {extProviderType !== null && (extRetailerId !== null || extManufacturerId !== null || extThirdPartyId !== null) && !extIsSubscription && (
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">Coverage Start Date <span className="text-muted-foreground font-normal">(optional)</span></label>
                      <Input type="date" value={extUserStartDate} onChange={e => setExtUserStartDate(e.target.value)} />
                      <p className="text-xs text-muted-foreground">Leave blank to use purchase date</p>
                    </div>
                  )}

                  {/* Deductible chips */}
                  {extProviderType !== null && (extRetailerId !== null || extManufacturerId !== null || extThirdPartyId !== null) && (() => {
                    const tier = extProviderType === 'manufacturer' && extManufacturerId !== 'other' ? (() => { const mfg3 = kManufacturerProviders.find(m => m.id === extManufacturerId); return mfg3?.isMultiTier ? mfg3.tiers.find(t => t.id === extPlanTierId) ?? null : mfg3?.defaultTier ?? null; })() : null;
                    if (tier?.deductibleNote) return <div className="rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 px-3 py-2.5"><p className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-0.5">{t('addDrawer.serviceFees')}</p><p className="text-xs text-blue-600 dark:text-blue-400">{tier.deductibleNote}</p></div>;
                    return (
                      <div className="space-y-1.5">
                        <p className="text-sm font-medium">{t('addDrawer.deductible')} <span className="text-muted-foreground font-normal">{t('addDrawer.optional')}</span></p>
                        <div className="flex flex-wrap gap-1.5">
                          {DEDUCTIBLE_CHIPS.map(amt => (<button key={amt} type="button" onClick={() => setExtDeductibleAmount(extDeductibleAmount === amt ? null : amt)} className={cn('px-3 py-1.5 rounded-lg text-xs font-medium border transition-all', extDeductibleAmount === amt ? 'bg-foreground text-background border-foreground' : 'text-foreground border-border hover:border-foreground/40 hover:bg-secondary/50 bg-card')}>${amt}</button>))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* ── Property ── */}
          {currentStep === 'property' && (
            <div className="space-y-5">
              <SectionIntro icon={<HomeIcon className="h-5 w-5 text-foreground" />} title={stepLabels['property']} subtitle={t('addDrawer.descProperty')} />

              <div className="space-y-2">
                {([
                  { value: 'keep' as const, label: t('addDrawer.propKeep'), sub: t('addDrawer.propKeepSub'), Icon: HomeIcon },
                  { value: 'move' as const, label: t('addDrawer.propMove'), sub: t('addDrawer.propMoveSub'), Icon: HomeIcon },
                  { value: 'create' as const, label: t('addDrawer.propCreate'), sub: asset.deliveryAddress || t('addDrawer.propCreateSubDefault'), Icon: PlusCircle },
                ]).map(({ value, label, sub, Icon }) => (
                  <button key={value} type="button" onClick={() => setPropertyAction(value)} className={cn('w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all duration-150', propertyAction === value ? 'border-foreground bg-secondary' : 'border-border bg-card hover:border-foreground/30 hover:bg-secondary/50')}>
                    <div className={cn('h-10 w-10 rounded-xl flex items-center justify-center shrink-0', propertyAction === value ? 'bg-foreground/10' : 'bg-muted')}><Icon className={cn('h-5 w-5', propertyAction === value ? 'text-foreground' : 'text-muted-foreground')} /></div>
                    <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-foreground">{label}</p><p className="text-xs text-muted-foreground truncate mt-0.5">{sub}</p></div>
                    <div className={cn('h-5 w-5 rounded-full border-2 shrink-0 flex items-center justify-center', propertyAction === value ? 'border-foreground bg-foreground' : 'border-muted-foreground')}>{propertyAction === value && <div className="h-2 w-2 rounded-full bg-background" />}</div>
                  </button>
                ))}
              </div>

              {propertyAction === 'move' && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">{t('addDrawer.selectHome')}</label>
                  <Select value={targetHomeId} onValueChange={setTargetHomeId}>
                    <SelectTrigger className="w-full h-11 rounded-xl text-sm"><SelectValue placeholder={t('addDrawer.chooseHomePh')} /></SelectTrigger>
                    <SelectContent>{properties.map(p => (<SelectItem key={p.id} value={p.id}>{p.name || p.address}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {/* ── Review ── */}
          {currentStep === 'review' && (
            <div className="space-y-5">
              <SectionIntro icon={<Check className="h-5 w-5 text-foreground" />} title={stepLabels['review']} subtitle={t('addDrawer.descReview')} />

              {/* Asset card */}
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="px-5 pt-4 pb-1">
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest mb-3">{t('addDrawer.reviewAsset')}</p>
                  <ReviewRow label={t('addDrawer.reviewAsset')} value={asset.name} />
                  <ReviewRow label={t('addDrawer.reviewPurchaseDate')} value={(() => { if (hasPurchaseDate) return fmt(resolvedPurchaseDate); if (purchaseMonth !== null) return `${MONTHS[purchaseMonth]} ${purchaseYear}`; return t('addDrawer.notSet'); })()} />
                </div>
              </div>

              {/* Coverage card */}
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="px-5 py-4">
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest mb-3">{t('addDrawer.reviewCoverage')}</p>
                  <div className="flex items-center gap-3 pb-3">
                    <div className="h-8 w-8 rounded-lg bg-foreground/10 flex items-center justify-center shrink-0"><Shield className="h-4 w-4 text-foreground" /></div>
                    <div><p className="text-sm font-medium text-foreground">{t('addDrawer.mfgWarranty')} <span className="ml-1 text-xs text-muted-foreground font-normal">{t('addDrawer.autoApplied')}</span></p><p className="text-xs text-muted-foreground">{t('addDrawer.oneYearExpires', { date: fmt(mfgExpiry) })}</p></div>
                  </div>
                  {extEnabled && extDisplayName && (
                    <div className="flex items-start gap-3 pt-3 border-t border-border">
                      <div className="h-8 w-8 rounded-lg bg-foreground/10 flex items-center justify-center shrink-0 mt-0.5"><ShieldCheck className="h-4 w-4 text-foreground" /></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{t('addDrawer.extendedProtection')}</p>
                        <p className="text-xs font-semibold text-foreground/80">{extDisplayName}</p>
                        {(() => { if (extIsSubscription && extMonthlyFee) return <p className="text-xs text-muted-foreground">${extMonthlyFee}/mo · {SERVICE_TYPE_LABELS[extServiceType]}</p>; if (extDurationYears) { const yrs = extDurationYears === 1 ? '' : 's'; const expiry2 = extExpiry ? ` · Expires ${fmt(extExpiry)}` : ''; return <p className="text-xs text-muted-foreground">{extDurationYears} yr{yrs} · {SERVICE_TYPE_LABELS[extServiceType]}{expiry2}</p>; } return null; })()}
                        <p className="text-xs text-muted-foreground mt-0.5">{extUserStartDate ? `Starts ${fmt(new Date(extUserStartDate + 'T00:00:00'))}` : extStartsOnPurchaseDate ? 'Starts from purchase date' : 'Starts after manufacturer warranty'}</p>
                        {extDeductibleAmount && extDeductibleAmount > 0 && <p className="text-xs text-muted-foreground">${extDeductibleAmount} deductible per claim</p>}
                        {extContractNumber && <p className="text-xs text-muted-foreground">Plan #: {extContractNumber.length > 4 ? `****${extContractNumber.slice(-4)}` : extContractNumber}</p>}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {saveError && <div className="flex items-start gap-2.5 p-4 rounded-xl bg-destructive/10 border border-destructive/20"><AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" /><p className="text-sm text-destructive">{saveError}</p></div>}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="shrink-0 border-t border-border px-6 py-4 flex items-center justify-between gap-3 bg-card">
          {currentStepIdx > 0 ? (
            <Button variant="outline" onClick={goBack} disabled={saving} className="flex items-center gap-1"><ChevronLeft className="h-4 w-4" /> Back</Button>
          ) : (
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          )}
          {currentStep === 'review' ? (
            <Button onClick={handleConfirm} disabled={saving} className="flex items-center gap-2">{saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</> : t('addDrawer.confirmSave')}</Button>
          ) : (
            <Button onClick={goNext} disabled={!canGoNext() || saving}>{t('addDrawer.continue')}</Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
